import Vue from 'vue'
import VueRouter from 'vue-router'
import { authApi } from '@/api/index'

Vue.use(VueRouter)

// session 验证缓存：避免每次路由跳转都请求后端
let sessionVerified = false
let verifyPromise = null

async function verifySession() {
  if (sessionVerified) return true
  // 如果已有验证请求进行中，复用同一个 Promise
  if (verifyPromise) return verifyPromise
  verifyPromise = authApi.userInfo().then(res => {
    sessionVerified = true
    // 同步更新 localStorage（后端返回的信息始终最新）
    localStorage.setItem('user', JSON.stringify(res.data))
    return true
  }).catch(e => {
    // 只有明确的 401/未登录 才清空本地信息
    if (e.message?.includes('未登录') || e.message?.includes('401')) {
      localStorage.removeItem('user')
      sessionVerified = false
      return false
    }
    // 网络超时/后端未启动等情况：不判失效，放行让后续 API 调用自行处理
    return true
  }).finally(() => {
    verifyPromise = null
  })
  return verifyPromise
}

/** 供外部调用：登出或 401 时清除 session 缓存 */
export function clearSessionCache() {
  sessionVerified = false
  localStorage.removeItem('user')
  localStorage.removeItem('token')
}

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { title: '登录', noAuth: true }
  },
  {
    path: '/',
    component: () => import('@/views/Layout.vue'),
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/Dashboard.vue'),
        meta: { title: '仪表盘' }
      },
      {
        path: 'buildings',
        name: 'BuildingManage',
        component: () => import('@/views/config/BuildingManage.vue'),
        meta: { title: '楼栋管理' }
      },
      {
        path: 'floors',
        name: 'FloorConfig',
        component: () => import('@/views/config/FloorConfig.vue'),
        meta: { title: '楼层管理' }
      },
      {
        path: 'meters',
        name: 'MeterManage',
        component: () => import('@/views/config/MeterManage.vue'),
        meta: { title: '仪表管理' }
      },
      {
        path: 'charge-config',
        name: 'ChargeConfig',
        component: () => import('@/views/config/ChargeConfig.vue'),
        meta: { title: '收费项目' }
      },
      {
        path: 'meter-reading',
        name: 'MeterReading',
        component: () => import('@/views/reading/MeterReading.vue'),
        meta: { title: '抄表录入' }
      },
      {
        path: 'bill-history',
        name: 'BillHistory',
        component: () => import('@/views/bill/BillHistory.vue'),
        meta: { title: '历史账单' }
      },
      {
        path: 'bill-generate',
        name: 'BillGenerate',
        component: () => import('@/views/bill/BillGenerate.vue'),
        meta: { title: '账单生成' }
      },
      {
        path: 'bill-detail/:id',
        name: 'BillDetail',
        component: () => import('@/views/bill/BillDetail.vue'),
        meta: { title: '账单详情' }
      },
      {
        path: 'statistics',
        name: 'Statistics',
        component: () => import('@/views/statistics/Statistics.vue'),
        meta: { title: '统计分析' }
      },
      {
        path: 'online-users',
        name: 'OnlineUsers',
        component: () => import('@/views/system/OnlineUsers.vue'),
        meta: { title: '在线用户', adminOnly: true }
      },
      {
        path: 'login-statistics',
        name: 'LoginStatistics',
        component: () => import('@/views/system/LoginStatistics.vue'),
        meta: { title: '登录统计', adminOnly: true }
      },
      {
        path: 'roles',
        name: 'RoleManage',
        component: () => import('@/views/system/RoleManage.vue'),
        meta: { title: '角色管理', adminOnly: true }
      },
      {
        path: 'users',
        name: 'UserManage',
        component: () => import('@/views/system/UserManage.vue'),
        meta: { title: '用户管理', adminOnly: true }
      },
      {
        path: 'menus',
        name: 'MenuManage',
        component: () => import('@/views/system/MenuManage.vue'),
        meta: { title: '菜单管理', adminOnly: true }
      },
      {
        path: 'rooms',
        name: 'RoomManage',
        component: () => import('@/views/config/RoomManage.vue'),
        meta: { title: '房间管理' }
      },
      {
        path: 'tenants',
        name: 'TenantManage',
        component: () => import('@/views/tenant/TenantManage.vue'),
        meta: { title: '租客管理' }
      },
      {
        path: 'goodies',
        name: 'Goodies',
        component: () => import('@/views/common/Goodies.vue'),
        meta: { title: '房东好物' }
      },
      {
        path: 'carousel-articles',
        name: 'CarouselArticle',
        component: () => import('@/views/config/CarouselArticle.vue'),
        meta: { title: '轮播文章' }
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/common/Profile.vue'),
        meta: { title: '个人设置' }
      },
      {
        path: 'feedback',
        name: 'Feedback',
        component: () => import('@/views/common/Feedback.vue'),
        meta: { title: '建议反馈' }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'hash',
  base: process.env.BASE_URL,
  routes
})

router.beforeEach(async (to, from, next) => {
  // 登录页直接放行
  if (to.meta.noAuth) {
    document.title = to.meta.title ? `${to.meta.title} - 可言寓` : '可言寓'
    return next()
  }

  const user = localStorage.getItem('user')
  // 无本地用户信息 → 直接去登录
  if (!user) {
    return next('/login')
  }

  // 有本地用户信息 → 首次进入时异步验证后端 token
  if (!sessionVerified) {
    const ok = await verifySession()
    if (!ok) {
      return next('/login')
    }
  }

  // 管理员专属路由检查
  if (to.meta.adminOnly) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      if (user.role !== 'ADMIN') {
        return next('/dashboard')
      }
    } catch {}
  }

  document.title = to.meta.title ? `${to.meta.title} - 可言寓` : '可言寓'
  next()
})

export default router
